/**
 * Ditto Profile Theme Engine
 * 
 * Derives all 19 CSS custom property tokens from just 3 core colors:
 * background, text, and primary.
 */

export interface CoreThemeColors {
  /** Background color (HSL string, e.g. "228 20% 10%") */
  background: string;
  /** Text/foreground color */
  text: string;
  /** Primary accent color (buttons, links, focus rings) */
  primary: string;
}

export interface DerivedThemeTokens {
  background: string;
  foreground: string;
  card: string;
  cardForeground: string;
  popover: string;
  popoverForeground: string;
  primary: string;
  primaryForeground: string;
  secondary: string;
  secondaryForeground: string;
  muted: string;
  mutedForeground: string;
  accent: string;
  accentForeground: string;
  destructive: string;
  destructiveForeground: string;
  border: string;
  input: string;
  ring: string;
}

interface HSL {
  h: number;
  s: number;
  l: number;
}

/** Parse an HSL string like "228 20% 10%" into components. */
function parseHSL(hsl: string): HSL {
  const parts = hsl.replace(/%/g, '').split(/\s+/).map(Number);
  return { h: parts[0] || 0, s: parts[1] || 0, l: parts[2] || 0 };
}

/** Format HSL components back to a CSS custom property string. */
function formatHSL(hsl: HSL): string {
  return `${Math.round(hsl.h)} ${Math.round(hsl.s)}% ${Math.round(hsl.l)}%`;
}

/** Clamp a value between min and max. */
function clamp(val: number, min: number, max: number): number {
  return Math.min(Math.max(val, min), max);
}

/** Check if a color is "dark" based on luminance. */
function isDark(hsl: HSL): boolean {
  return hsl.l < 50;
}

/** Lighten an HSL color by a percentage. */
function lighten(hsl: HSL, amount: number): HSL {
  return { ...hsl, l: clamp(hsl.l + amount, 0, 100) };
}

/** Darken an HSL color by a percentage. */
function darken(hsl: HSL, amount: number): HSL {
  return { ...hsl, l: clamp(hsl.l - amount, 0, 100) };
}

/** Desaturate an HSL color. */
function desaturate(hsl: HSL, amount: number): HSL {
  return { ...hsl, s: clamp(hsl.s - amount, 0, 100) };
}

/**
 * Derive all 19 theme tokens from 3 core colors.
 */
export function deriveTokensFromCore(
  bgStr: string,
  textStr: string,
  primaryStr: string,
): DerivedThemeTokens {
  const bg = parseHSL(bgStr);
  const text = parseHSL(textStr);
  const primary = parseHSL(primaryStr);
  const dark = isDark(bg);

  // Card: slightly lighter bg for dark mode, same for light
  const card = dark ? lighten(bg, 4) : bg;
  const popover = card;

  // Secondary / Muted: darker bg for light, lighter for dark
  const secondary = dark ? lighten(bg, 10) : darken(bg, 4);
  const muted = secondary;

  // Muted foreground: desaturated, mid-luminance
  const mutedFg: HSL = {
    h: text.h,
    s: clamp(text.s - 25, 0, 100),
    l: dark ? 55 : 46,
  };

  // Border: primary hue + reduced saturation
  const border: HSL = {
    h: primary.h,
    s: clamp(primary.s - 45, 5, 100),
    l: dark ? 20 : 85,
  };

  // Primary foreground: white for dark primary, dark for light primary
  const primaryFg: HSL = isDark(primary) 
    ? { h: text.h, s: text.s, l: 98 }
    : { h: bg.h, s: bg.s, l: 5 };

  return {
    background: formatHSL(bg),
    foreground: formatHSL(text),
    card: formatHSL(card),
    cardForeground: formatHSL(text),
    popover: formatHSL(popover),
    popoverForeground: formatHSL(text),
    primary: formatHSL(primary),
    primaryForeground: formatHSL(primaryFg),
    secondary: formatHSL(secondary),
    secondaryForeground: formatHSL(text),
    muted: formatHSL(muted),
    mutedForeground: formatHSL(mutedFg),
    accent: formatHSL(primary),
    accentForeground: formatHSL(text),
    destructive: '0 72% 51%',
    destructiveForeground: '0 0% 98%',
    border: formatHSL(border),
    input: formatHSL(border),
    ring: formatHSL(primary),
  };
}

/** Convert a camelCase token name to its CSS custom property name. */
export function tokenToCssVar(token: string): string {
  // cardForeground → card-foreground
  return '--' + token.replace(/([A-Z])/g, '-$1').toLowerCase();
}

/** Convert hex color to HSL string. */
export function hexToHslString(hex: string): string {
  // Remove # prefix
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.substring(0, 2), 16) / 255;
  const g = parseInt(hex.substring(2, 4), 16) / 255;
  const b = parseInt(hex.substring(4, 6), 16) / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return `0 0% ${Math.round(l * 100)}%`;
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h: number;
  if (max === r) {
    h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
  } else if (max === g) {
    h = ((b - r) / d + 2) / 6;
  } else {
    h = ((r - g) / d + 4) / 6;
  }

  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Convert HSL string to hex color. */
export function hslStringToHex(hsl: string): string {
  const { h, s, l } = parseHSL(hsl);
  const sNorm = s / 100;
  const lNorm = l / 100;

  const a = sNorm * Math.min(lNorm, 1 - lNorm);

  const f = (n: number) => {
    const k = (n + h / 30) % 12;
    const color = lNorm - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };

  return `#${f(0)}${f(8)}${f(4)}`;
}

/** Generate CSS custom properties object from tokens. */
export function tokensToCssVars(tokens: DerivedThemeTokens): Record<string, string> {
  return Object.fromEntries(
    Object.entries(tokens).map(([key, value]) => [tokenToCssVar(key), value]),
  );
}

/** Parse a kind 16767 active profile theme event into core colors. */
export function parseProfileThemeEvent(tags: string[][]): CoreThemeColors | null {
  const colors: Partial<Record<string, string>> = {};

  for (const tag of tags) {
    if (tag[0] === 'c' && tag[1] && tag[2]) {
      const hex = tag[1];
      const role = tag[2]; // 'background', 'text', 'primary'
      colors[role] = hexToHslString(hex);
    }
  }

  if (colors.background && colors.text && colors.primary) {
    return {
      background: colors.background,
      text: colors.text,
      primary: colors.primary,
    };
  }

  return null;
}

/** Theme presets following the Ditto philosophy. */
export const THEME_PRESETS: { name: string; colors: CoreThemeColors; emoji: string }[] = [
  {
    name: 'Midnight Galaxy',
    emoji: '🌌',
    colors: {
      background: '260 20% 6%',
      text: '240 20% 92%',
      primary: '270 70% 60%',
    },
  },
  {
    name: 'Ocean Depths',
    emoji: '🌊',
    colors: {
      background: '200 30% 8%',
      text: '195 25% 90%',
      primary: '185 70% 45%',
    },
  },
  {
    name: 'Solar Flare',
    emoji: '☀️',
    colors: {
      background: '25 30% 6%',
      text: '40 30% 92%',
      primary: '35 90% 55%',
    },
  },
  {
    name: 'Enchanted Forest',
    emoji: '🌿',
    colors: {
      background: '150 25% 7%',
      text: '140 20% 90%',
      primary: '145 60% 45%',
    },
  },
  {
    name: 'Rose Quartz',
    emoji: '🌸',
    colors: {
      background: '340 20% 8%',
      text: '330 20% 92%',
      primary: '340 70% 65%',
    },
  },
  {
    name: 'Neon Arcade',
    emoji: '🕹️',
    colors: {
      background: '240 15% 4%',
      text: '120 80% 85%',
      primary: '120 80% 50%',
    },
  },
  {
    name: 'Crimson Eclipse',
    emoji: '🔮',
    colors: {
      background: '0 15% 6%',
      text: '0 10% 90%',
      primary: '0 70% 55%',
    },
  },
  {
    name: 'Arctic Light',
    emoji: '❄️',
    colors: {
      background: '210 15% 95%',
      text: '220 25% 12%',
      primary: '220 70% 55%',
    },
  },
];
